import Axios, { AxiosResponse, AxiosRequestConfig, AxiosInstance } from "axios";
import Shared from "@/common/shared";
import { Environment } from "@/common/environment";
import { getModule } from "vuex-module-decorators";
import StoreVuexModule from "@/store/StoreVuexModule";
import store from "@/store";
import OidcService from "./oidc.service";

export function getAccessToken(): string {
  const s = getModule(StoreVuexModule, store);
  return s.apiToken;
}

/**
 * Create custom instance of Axios with custom changes
 *
 * @returns {AxiosInstance}
 */
export function createAxiosInstance(oidcService: OidcService, environment: Environment): AxiosInstance {
  function fnServiceUnavailable(): void {
    Shared.showToastMessage(Shared.messages.SERVICE_UNAVAILABLE, "is-danger");
  }

  const axios = Axios.create({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transformResponse: (data: any): any => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let res: any = data;
      try {
        if (typeof data !== "string") {
          // if string pass as it is.
          return data;
        }
        // else json parse the response
        res = JSON.parse(data);
      } catch (ex) {
        res = data;
      }
      return res;
    },
    validateStatus: (status: number) =>
      (status >= 200 && status < 300) || status === 404 || status === 401 || (status >= 500 && status < 509),
  });

  axios.interceptors.request.use((config: AxiosRequestConfig) => {
    return new Promise((resolve) => {
      // set base URL
      config.baseURL = environment.ApiBaseUrl;
      oidcService.getAccessToken().then((token) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        resolve(config);
      });
    });
  });

  axios.interceptors.response.use(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (res: AxiosResponse<any>) => {
      // server error codes
      if (res.status >= 500 && res.status <= 509) {
        fnServiceUnavailable();
        res.data = null;
        return Promise.reject(res);
      }
      if (res.status === 404) {
        res.data = null;
      }

      // unauthorized access
      if (typeof res.data === "string" && res.data !== "" && res.status === 401) {
        return res;
      }

      // session expired
      if (res.status === 401) {
        // renew token and retry
        return oidcService
          .renewToken()
          .then(() => axios.request(res.config))
          .catch(() => Promise.reject(res));
      }

      return res;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error: any) => {
      if (error.message && error.message === "Network Error") {
        fnServiceUnavailable();
      }

      if (error.response.status === 409) {
        return Promise.resolve(error.response);
      }

      Shared.showToastMessage(
        typeof error.response.data === "string" ? error.response.data : Shared.messages.SOMETHING_WRONG,
        "is-danger",
      );

      return Promise.reject(error);
    },
  );

  // return instance
  return axios;
}
